import { useRef, useState } from 'react';
import { IoMdClose } from 'react-icons/io';

import InputField from 'components/fields/InputField';
import CreateButton from 'components/buttons/CreateButton';
import { sendCatalogInterest } from 'interfaces/autoOrder';

const CatalogInterestModal = (props: {
    isOpen: boolean;
    onClose: () => void;
    supplier: string;
}) => {
    const { isOpen, onClose, supplier } = props;
    const [firstName, setFirstName] = useState('');
    const [phone, setPhone] = useState('');
    const [company, setCompany] = useState('');
    const [email, setEmail] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const [error, setError] = useState('');
    // Synchronous guard against double-clicks/double-taps: isSending is React
    // state, so two clicks fired before the first re-render both pass the
    // `isSending ? undefined : submit` check below. A ref is set immediately,
    // before any await, so the second call is rejected regardless of timing.
    const isSubmittingRef = useRef(false);

    if (!isOpen) return null;

    const handleClose = () => {
        setFirstName('');
        setPhone('');
        setCompany('');
        setEmail('');
        setIsSent(false);
        setError('');
        onClose();
    };

    const submit = async () => {
        if (isSubmittingRef.current) return;

        if (!firstName || !phone || !company || !email) {
            setError('Merci de remplir tous les champs.');
            return;
        }
        isSubmittingRef.current = true;
        setError('');
        setIsSending(true);
        const res = await sendCatalogInterest({
            firstName,
            phone,
            company,
            email,
            supplier,
        });
        isSubmittingRef.current = false;
        setIsSending(false);
        if (res) setIsSent(true);
        else setError('Une erreur est survenue, merci de réessayer.');
    };

    return (
        <div className="fixed inset-0 z-[1003] flex items-center justify-center p-4">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative z-[1004] w-full max-w-md animate-[slideDown_0.3s_ease-out]">
                <div className="relative overflow-hidden rounded-3xl bg-white p-6 shadow-2xl">
                    <button
                        onClick={handleClose}
                        className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 transition-all hover:bg-gray-200"
                    >
                        <IoMdClose size={18} className="text-navy-700" />
                    </button>

                    {!isSent ? (
                        <>
                            <h2 className="pr-8 text-2xl font-bold text-navy-700">
                                Intéressé(e) par le catalogue ?
                            </h2>
                            <p className="mt-2 text-sm text-gray-600">
                                Laissez-nous vos coordonnées, un commercial
                                vous recontactera pour vous donner accès à la
                                boutique.
                            </p>

                            <div className="mt-5 flex flex-col gap-3">
                                <InputField
                                    variant="auth"
                                    id="interest-firstname"
                                    label="Prénom"
                                    placeholder="Marc"
                                    type="text"
                                    value={firstName}
                                    onChange={(e: any) =>
                                        setFirstName(e.target.value)
                                    }
                                />
                                <InputField
                                    variant="auth"
                                    id="interest-company"
                                    label="Société"
                                    placeholder="Votre entreprise"
                                    type="text"
                                    value={company}
                                    onChange={(e: any) =>
                                        setCompany(e.target.value)
                                    }
                                />
                                <InputField
                                    variant="auth"
                                    id="interest-phone"
                                    label="Téléphone"
                                    placeholder="06 12 34 56 78"
                                    type="tel"
                                    value={phone}
                                    onChange={(e: any) =>
                                        setPhone(e.target.value)
                                    }
                                />
                                <InputField
                                    variant="auth"
                                    id="interest-email"
                                    label="Adresse mail"
                                    placeholder="marc@votre-entreprise.com"
                                    type="email"
                                    value={email}
                                    onChange={(e: any) =>
                                        setEmail(e.target.value)
                                    }
                                />
                            </div>

                            {error && (
                                <p className="mt-3 text-sm text-red-500">
                                    {error}
                                </p>
                            )}

                            <CreateButton
                                extra="mt-5 w-full"
                                value={
                                    isSending ? 'Envoi...' : 'Envoyer ma demande'
                                }
                                onClick={isSending ? undefined : submit}
                            />
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-4 text-center">
                            <p className="text-[70px]">🙌</p>
                            <p className="text-xl font-bold text-navy-700">
                                Merci !
                            </p>
                            <p className="mt-2 text-sm text-gray-600">
                                Votre demande a bien été envoyée. Un commercial
                                va vous recontacter pour vous donner accès à
                                la boutique.
                            </p>
                            <CreateButton
                                extra="mt-5 w-full"
                                value="Fermer"
                                onClick={handleClose}
                            />
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default CatalogInterestModal;
